import type { ApiErrorDetail } from '@klareospace/pharos-lib-types';
import type { TFunction } from 'i18next';

// ─── Types ────────────────────────────────────────────────────────────────────

type OrderUpdateErrorDetail = Pick<ApiErrorDetail, 'errorCode' | 'message'>;

// ─── Constants ────────────────────────────────────────────────────────────────

/** Backend codes on which the drawer must refetch the order so editability locks refresh. */
const REFETCH_ON_ERROR_CODES: ReadonlySet<string> = new Set(['Conflict.OrderInExecution', 'Conflict.OrderTerminal']);

// Namespace-prefixed so the mapper works regardless of the caller's default i18next namespace.
const ERROR_MESSAGE_KEYS: Record<string, string> = {
  'BadRequest.OrderUpdateIneligibleValue': 'orders:drawer.errors.orderUpdate.ineligibleValue',
  'BadRequest.OrderUpdateNonEditableField': 'orders:drawer.errors.orderUpdate.nonEditableField',
  'Conflict.OrderInExecution': 'orders:common.inProgressTooltip',
  'Conflict.OrderTerminal': 'orders:drawer.errors.orderUpdate.terminal',
  'Conflict.OrderUpdateConflict': 'orders:drawer.errors.orderUpdate.conflict',
  'NotFound.Carrier': 'orders:drawer.errors.orderUpdate.carrierNotFound',
};

// ─── Utils ────────────────────────────────────────────────────────────────────

/** Maps a backend error code to a user-facing message; falls back to the API message, then a generic key. */
export function resolveOrderUpdateErrorMessage(t: TFunction, detail: OrderUpdateErrorDetail): string {
  const key = detail.errorCode ? ERROR_MESSAGE_KEYS[detail.errorCode] : undefined;
  if (key) return t(key);
  return detail.message || t('orders:drawer.errors.orderUpdate.generic');
}

/** Whether the order detail should be refetched after this rejection so editability locks refresh. */
export function shouldRefetchOnOrderUpdateError(errorCode: string | undefined): boolean {
  return errorCode !== undefined && REFETCH_ON_ERROR_CODES.has(errorCode);
}
